/**
 * Capacity & Utilisation data layer (task 14.2, R6, R7.1, R7.6, R7.7).
 *
 * Wired to `POST /api/manpower/capacity-utilization`, which computes
 * Capacity_Hours, Demand_Hours, Utilization and Bottleneck_State across the
 * planning year's periods (R6.1-R6.4).
 *
 * ## Scope, including "every role" and "every Region"
 *
 * `ffRole` and `regionId` are each optional on the endpoint, and omitting one
 * aggregates across every value of it. This layer expresses that with the
 * {@link ALL_ROLES}/{@link ALL_REGIONS} sentinels rather than `undefined`, so a
 * screen's filter state distinguishes "the reader chose every Region" from "the
 * Region list has not loaded yet" — two things that look identical as `undefined`
 * and mean opposite things for whether a request should fire.
 *
 * The aggregation itself is the server's. Capacity_Hours summed across scopes and
 * the Bottleneck_State classified against the configured threshold have to happen
 * in one place, because R6.6 makes "changing the threshold reclassifies on the
 * next read" true only if there is a single classifier; a client that summed the
 * per-scope responses itself would be a second one.
 *
 * ## Why a 404 is data, not an error
 *
 * The route answers 404 when no Capacity_Period matches the scope, which is the
 * honest answer rather than zero-hours rows. {@link useCapacityUtilisation}
 * translates that into an empty period list so the screen renders its "no data"
 * state instead of an error alert.
 */
import type { BottleneckState, FieldForceRole } from '@complan/domain';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetch } from '@/shared/api/client';
import { ApiRequestError } from '@/shared/api/api-error';
import { manpowerQueryKeys } from '../manpower-query-keys';

const NOT_FOUND_STATUS = 404;

/** Every FF role, aggregated — the endpoint's "omit `ffRole`". */
export const ALL_ROLES = 'all';
/** Every Region, aggregated — the endpoint's "omit `regionId`". */
export const ALL_REGIONS = 'all';

/** An FF role, or every role at once. */
export type FfRoleScope = FieldForceRole | typeof ALL_ROLES;
/** A Region id, or every Region at once. */
export type RegionScope = string | typeof ALL_REGIONS;

/** Request parameters for the Capacity & Utilisation calculation (R6, R7.1). */
export interface CapacityUtilisationParams {
  readonly planningYear: number;
  readonly ffRole: FfRoleScope;
  readonly regionId: RegionScope;
}

/** The Capacity_Period inputs behind one scope's contribution to a period (R3.5). */
export interface CapacityInputRow {
  readonly capacityPeriodId: string;
  readonly ffRole: FieldForceRole;
  readonly regionId: string;
  readonly headcount: number | null;
  readonly workdays: number;
  readonly oofDays: number;
  readonly productiveHoursPerWorkday: number;
  readonly availabilityFactor: number;
}

/** One Initiative's contribution to a period's Demand_Hours (R6.5's drill-through). */
export interface PeriodInitiative {
  readonly initiativeId: string;
  readonly initiativeRef: string;
  readonly title: string;
  readonly demandHours: number;
  readonly driverCount: number;
}

/**
 * One FF role's slice of a period: its own capacity, demand and classification
 * (R6.2-R6.4, R7.1).
 *
 * Present because capacity is held per role, so this is the figure that corresponds
 * to a real, separately staffed pool. A period totalling 70% can hide one role at
 * 130% and another at 20%, and only the per-role rows show that.
 */
export interface PeriodRoleUtilisation {
  readonly ffRole: string;
  readonly capacityHours: number | null;
  readonly dataIncompleteScopeCount: number;
  readonly demandHours: number;
  readonly utilisation: number | null;
  readonly bottleneckState: BottleneckState;
  readonly contributingInitiatives: readonly PeriodInitiative[];
}

/**
 * One period's capacity, demand and utilization (R6.2-R6.5).
 *
 * `capacityHours` and `utilisation` are `null` for the Data_Incomplete case — the
 * wire sends the string `'data_incomplete'` (R3.4) and this layer normalises it to
 * `null` so the view model has one absent-value shape to branch on.
 */
export interface UtilisationPeriod {
  /** The period's effective range, which is its identity across scopes. */
  readonly periodId: string;
  /** Every Capacity_Period record behind this period: one per scope in view. */
  readonly capacityPeriodIds: readonly string[];
  readonly effectivePeriodStart: string;
  readonly effectivePeriodEnd: string;
  readonly capacityHours: number | null;
  /** Contributing scopes excluded from `capacityHours` for want of a headcount (R3.4). */
  readonly dataIncompleteScopeCount: number;
  readonly demandHours: number;
  readonly utilisation: number | null;
  readonly bottleneckState: BottleneckState;
  /** This period's Demand_Hours by role; a role contributing nothing is absent. */
  readonly demandByRole: Readonly<Record<string, number>>;
  readonly capacityInputs: readonly CapacityInputRow[];
  readonly contributingInitiatives: readonly PeriodInitiative[];
  /** This period per FF role, in role order; a role with no pool here is absent. */
  readonly byRole: readonly PeriodRoleUtilisation[];
}

/** Response from the Capacity & Utilisation calculation endpoint (R6.5, R7.1). */
export interface CapacityUtilisationResponse {
  /** `null` when the result aggregates every role. */
  readonly ffRole: FieldForceRole | null;
  /** `null` when the result aggregates every Region. */
  readonly regionId: string | null;
  readonly periods: readonly UtilisationPeriod[];
  readonly contributingDriverCount: number;
  readonly contributingInitiativeCount: number;
  /** The Capacity_Threshold the Warning classifications were made against (R6.4). */
  readonly capacityThreshold: number;
  readonly demandByRole: Readonly<Record<string, number>>;
  readonly ffRoles: readonly FieldForceRole[];
  readonly regionCount: number;
  readonly granularity: string | null;
}

/** `number | 'data_incomplete'` on the wire, `number | null` here. */
const incompletableNumberSchema = z
  .union([z.number(), z.literal('data_incomplete')])
  .transform((value) => (value === 'data_incomplete' ? null : value));

const roleTotalsSchema = z.record(z.number()).default({});

const capacityInputSchema = z.object({
  capacityPeriodId: z.string(),
  ffRole: z.string(),
  regionId: z.string(),
  headcount: z.number().nullable().default(null),
  workdays: z.number(),
  oofDays: z.number(),
  productiveHoursPerWorkday: z.number(),
  availabilityFactor: z.number(),
});

const periodInitiativeSchema = z.object({
  initiativeId: z.string(),
  initiativeRef: z.string(),
  title: z.string(),
  demandHours: z.number(),
  driverCount: z.number().int().min(0).default(0),
});

const wireResponseSchema = z.object({
  ffRole: z.string().nullable().default(null),
  regionId: z.string().nullable().default(null),
  periods: z
    .array(
      z.object({
        periodId: z.string(),
        capacityPeriodIds: z.array(z.string()).default([]),
        effectivePeriodStart: z.string(),
        effectivePeriodEnd: z.string(),
        capacityHours: incompletableNumberSchema,
        dataIncompleteScopeCount: z.number().int().min(0).default(0),
        demandHours: z.number(),
        utilization: incompletableNumberSchema,
        bottleneckState: z.enum(['normal', 'warning', 'over_capacity', 'data_incomplete']),
        demandByRole: roleTotalsSchema,
        capacityInputs: z.array(capacityInputSchema).default([]),
        contributingInitiatives: z.array(periodInitiativeSchema).default([]),
        byRole: z
          .array(
            z.object({
              ffRole: z.string(),
              capacityHours: incompletableNumberSchema,
              dataIncompleteScopeCount: z.number().int().min(0).default(0),
              demandHours: z.number(),
              utilization: incompletableNumberSchema,
              bottleneckState: z.enum([
                'normal',
                'warning',
                'over_capacity',
                'data_incomplete',
              ]),
              contributingInitiatives: z.array(periodInitiativeSchema).default([]),
            }),
          )
          .default([]),
      }),
    )
    .default([]),
  contributingDriverCount: z.number().int().min(0).default(0),
  contributingInitiativeCount: z.number().int().min(0).default(0),
  capacityThreshold: z.number(),
  demandByRole: roleTotalsSchema,
  ffRoles: z.array(z.string()).default([]),
  regionCount: z.number().int().min(0).default(0),
  grid: z.object({ granularity: z.string() }).partial().default({}),
});

/**
 * The empty result a 404 (no Capacity_Period for the scope) resolves to.
 *
 * `capacityThreshold` is 0 rather than a guessed 85: nothing was calculated, so
 * there is no threshold this result was classified against, and a plausible-looking
 * default would be reported on screen as though it had come from configuration.
 * The screen renders its no-data state for an empty `periods`, so the figure is
 * never shown.
 */
const EMPTY_RESPONSE: CapacityUtilisationResponse = {
  ffRole: null,
  regionId: null,
  periods: [],
  contributingDriverCount: 0,
  contributingInitiativeCount: 0,
  capacityThreshold: 0,
  demandByRole: {},
  ffRoles: [],
  regionCount: 0,
  granularity: null,
};

/**
 * Fetches Capacity & Utilisation for a scope (R6, R7.1).
 *
 * A 404 resolves to {@link EMPTY_RESPONSE} rather than throwing: no
 * Capacity_Period for the scope is an answer the screen renders, not a failure.
 */
export async function fetchCapacityUtilisation(
  params: CapacityUtilisationParams,
): Promise<CapacityUtilisationResponse> {
  let body: unknown;
  try {
    body = await apiFetch<unknown>('/api/manpower/capacity-utilization', {
      method: 'POST',
      // An omitted key is what asks the endpoint to aggregate; sending
      // `ffRole: undefined` would serialise to no key anyway, but spreading says
      // so explicitly rather than relying on `JSON.stringify`'s behaviour.
      body: JSON.stringify({
        planningYear: params.planningYear,
        ...(params.ffRole !== ALL_ROLES && { ffRole: params.ffRole }),
        ...(params.regionId !== ALL_REGIONS && { regionId: params.regionId }),
      }),
    });
  } catch (cause) {
    if (cause instanceof ApiRequestError && cause.status === NOT_FOUND_STATUS) {
      return EMPTY_RESPONSE;
    }
    throw cause;
  }

  const parsed = wireResponseSchema.parse(body);
  return {
    ffRole: parsed.ffRole as FieldForceRole | null,
    regionId: parsed.regionId,
    periods: parsed.periods.map((period) => ({
      periodId: period.periodId,
      capacityPeriodIds: period.capacityPeriodIds,
      effectivePeriodStart: period.effectivePeriodStart,
      effectivePeriodEnd: period.effectivePeriodEnd,
      capacityHours: period.capacityHours,
      dataIncompleteScopeCount: period.dataIncompleteScopeCount,
      demandHours: period.demandHours,
      utilisation: period.utilization,
      bottleneckState: period.bottleneckState,
      demandByRole: period.demandByRole,
      capacityInputs: period.capacityInputs.map((input) => ({
        ...input,
        ffRole: input.ffRole as FieldForceRole,
      })),
      contributingInitiatives: period.contributingInitiatives,
      byRole: period.byRole.map((role) => ({
        ffRole: role.ffRole,
        capacityHours: role.capacityHours,
        dataIncompleteScopeCount: role.dataIncompleteScopeCount,
        demandHours: role.demandHours,
        utilisation: role.utilization,
        bottleneckState: role.bottleneckState,
        contributingInitiatives: role.contributingInitiatives,
      })),
    })),
    contributingDriverCount: parsed.contributingDriverCount,
    contributingInitiativeCount: parsed.contributingInitiativeCount,
    capacityThreshold: parsed.capacityThreshold,
    demandByRole: parsed.demandByRole,
    ffRoles: parsed.ffRoles as readonly FieldForceRole[],
    regionCount: parsed.regionCount,
    granularity: parsed.grid.granularity ?? null,
  };
}

/**
 * React Query hook for the Capacity & Utilisation calculation (R6, R7.1).
 *
 * Keyed on the full scope so a filter change re-fetches rather than showing stale
 * data. Unlike the earlier single-scope version this needs no `enabled` guard for a
 * missing Region: "every Region" is a valid scope, so there is nothing to wait for.
 */
export function useCapacityUtilisation(
  params: CapacityUtilisationParams,
  options?: { readonly enabled?: boolean },
): UseQueryResult<CapacityUtilisationResponse, unknown> {
  return useQuery({
    queryKey: manpowerQueryKeys.capacityUtilisation({
      planningYear: params.planningYear,
      ffRole: params.ffRole,
      regionId: params.regionId,
    }),
    queryFn: () => fetchCapacityUtilisation(params),
    enabled: options?.enabled ?? true,
  });
}
